import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/Button';
import { AlertCircleIcon, CheckCircleIcon, KeyIcon, MailIcon, RefreshCwIcon, ShieldIcon, WifiOffIcon, DatabaseIcon, LockIcon, UserIcon, CookieIcon, ServerIcon, SparklesIcon } from 'lucide-react';
type TroubleshootingStep = 'initial' | 'account-check' | 'browser-check' | 'network-check' | 'solution';
export function LoginTroubleshooting() {
  const [currentStep, setCurrentStep] = useState<TroubleshootingStep>('initial');
  const [formData, setFormData] = useState({
    email: '',
    errorMessage: '',
    browser: '',
    operatingSystem: '',
    triedIncognito: 'no',
    usingVpn: 'no',
    clearedCache: 'no',
    disabledExtensions: 'no'
  });
  const [diagnosis, setDiagnosis] = useState<string[]>([]);
  const [solutions, setSolutions] = useState<string[]>([]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleNextStep = (nextStep: TroubleshootingStep) => {
    setCurrentStep(nextStep);
    window.scrollTo(0, 0);
  };
  const analyzeProblem = () => {
    const newDiagnosis: string[] = [];
    const newSolutions: string[] = [];
    // Check for common issues based on the form data
    if (formData.errorMessage.toLowerCase().includes('password')) {
      newDiagnosis.push('Incorrect password or account credentials');
      newSolutions.push('Reset your password using the "Forgot Password" link on the login page');
    }
    if (formData.errorMessage.toLowerCase().includes('not found') || formData.errorMessage.toLowerCase().includes('no account')) {
      newDiagnosis.push('Account may not exist with the provided email');
      newSolutions.push('Create a new account using the registration page');
      newSolutions.push('Double-check your email address for typos');
    }
    if (formData.usingVpn === 'yes') {
      newDiagnosis.push('VPN connection may be interfering with authentication');
      newSolutions.push('Temporarily disable your VPN and try logging in again');
    }
    if (formData.clearedCache === 'no') {
      newDiagnosis.push('Browser cache or cookies may be corrupted');
      newSolutions.push('Clear your browser cache and cookies, then try again');
    }
    if (formData.disabledExtensions === 'no') {
      newDiagnosis.push('Browser extensions might be blocking authentication');
      newSolutions.push('Temporarily disable all browser extensions and try again');
    }
    if (formData.triedIncognito === 'no') {
      newSolutions.push("Try logging in using your browser's incognito/private mode");
    }
    // If no specific issues identified, provide general solutions
    if (newDiagnosis.length === 0) {
      newDiagnosis.push('General authentication issue');
      newSolutions.push('Try logging in from a different device or browser');
      newSolutions.push('Check if the SponsrAI service is experiencing downtime');
      newSolutions.push('Contact support if the issue persists');
    }
    setDiagnosis(newDiagnosis);
    setSolutions(newSolutions);
    handleNextStep('solution');
  };
  return <Layout data-id="element-2733">
      <div className="min-h-screen w-full bg-white" data-id="element-2734">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-2735">
          {/* Header */}
          <div className="text-center mb-10 relative" data-id="element-2736">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-6 w-32 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" data-id="element-2737"></div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center" data-id="element-2738">
              <KeyIcon className="h-6 w-6 text-indigo-600 mr-2" data-id="element-2739" />
              Login Troubleshooting
            </h1>
            <p className="text-gray-600" data-id="element-2740">
              Let's help you solve your login issues and get you back into your
              account.
            </p>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mt-6 w-32 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" data-id="element-2741"></div>
          </div>
          {/* Main content - changes based on current step */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8 relative overflow-hidden" data-id="element-2742">
            {/* Mystical decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-50 rounded-full -mr-20 -mt-20 opacity-30" data-id="element-2743"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-50 rounded-full -ml-16 -mb-16 opacity-30" data-id="element-2744"></div>
            {/* Subtle arcane pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-100/10 via-transparent to-transparent opacity-40" data-id="element-2745"></div>
            <div className="relative z-10" data-id="element-2746">
              {currentStep === 'initial' && <div data-id="element-2747">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2748">
                    <SparklesIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2749" />
                    What's happening?
                  </h2>
                  <div className="space-y-6" data-id="element-2750">
                    <div data-id="element-2751">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2752">
                        Your Email Address
                      </label>
                      <div className="relative" data-id="element-2753">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2754">
                          <MailIcon className="h-5 w-5 text-gray-400" data-id="element-2755" />
                        </div>
                        <input type="email" name="email" className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Enter the email you're trying to use" value={formData.email} onChange={handleInputChange} data-id="element-2756" />
                      </div>
                    </div>
                    <div data-id="element-2757">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2758">
                        Error Message (if any)
                      </label>
                      <textarea name="errorMessage" rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Copy and paste any error message you see" value={formData.errorMessage} onChange={handleInputChange} data-id="element-2759"></textarea>
                    </div>
                    <div className="flex justify-end" data-id="element-2760">
                      <Button onClick={() => handleNextStep('account-check')} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2761">
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>}
              {currentStep === 'account-check' && <div data-id="element-2762">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2763">
                    <UserIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2764" />
                    Account Check
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100" data-id="element-2765">
                    <p className="text-sm text-indigo-700" data-id="element-2766">
                      Let's first check if we can find your account and verify
                      its status.
                    </p>
                  </div>
                  <div className="space-y-4 mb-6" data-id="element-2767">
                    <div className="flex items-start p-4 border border-gray-200 rounded-lg bg-white" data-id="element-2768">
                      <div className="flex-shrink-0 pt-0.5" data-id="element-2769">
                        <DatabaseIcon className="h-5 w-5 text-indigo-500" data-id="element-2770" />
                      </div>
                      <div className="ml-3" data-id="element-2771">
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-2772">
                          Account Lookup
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-2773">
                          We'll check if an account exists with email:{' '}
                          <span className="font-medium text-gray-700" data-id="element-2774">
                            {formData.email || '[No email provided]'}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start p-4 border border-gray-200 rounded-lg bg-white" data-id="element-2775">
                      <div className="flex-shrink-0 pt-0.5" data-id="element-2776">
                        <LockIcon className="h-5 w-5 text-indigo-500" data-id="element-2777" />
                      </div>
                      <div className="ml-3" data-id="element-2778">
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-2779">
                          Account Status
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-2780">
                          We'll verify if your account is active and not locked
                          due to too many failed login attempts.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 mb-6" data-id="element-2781">
                    <div data-id="element-2782">
                      <p className="text-sm text-gray-700 mb-2" data-id="element-2783">
                        Have you tried resetting your password?
                      </p>
                      <div className="flex space-x-4" data-id="element-2784">
                        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => window.open('/login', '_blank')} data-id="element-2785">
                          Try Password Reset
                        </button>
                        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => window.open('/register', '_blank')} data-id="element-2786">
                          Create New Account
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between" data-id="element-2787">
                    <Button onClick={() => handleNextStep('initial')} variant="outline" data-id="element-2788">
                      Back
                    </Button>
                    <Button onClick={() => handleNextStep('browser-check')} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2789">
                      Continue
                    </Button>
                  </div>
                </div>}
              {currentStep === 'browser-check' && <div data-id="element-2790">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2791">
                    <div className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2792" />
                    Browser & System Check
                  </h2>
                  <div className="space-y-6" data-id="element-2793">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2794">
                      <div data-id="element-2795">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2796">
                          Browser and Version
                        </label>
                        <input type="text" name="browser" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g. Chrome 94, Firefox 92" value={formData.browser} onChange={handleInputChange} data-id="element-2797" />
                      </div>
                      <div data-id="element-2798">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2799">
                          Operating System
                        </label>
                        <input type="text" name="operatingSystem" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g. Windows 10, macOS, iOS" value={formData.operatingSystem} onChange={handleInputChange} data-id="element-2800" />
                      </div>
                    </div>
                    <div className="space-y-4" data-id="element-2801">
                      <div data-id="element-2802">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2803">
                          Have you tried using incognito/private mode?
                        </label>
                        <div className="flex space-x-4" data-id="element-2804">
                          <label className="inline-flex items-center" data-id="element-2805">
                            <input type="radio" name="triedIncognito" value="yes" checked={formData.triedIncognito === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2806" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2807">
                              Yes
                            </span>
                          </label>
                          <label className="inline-flex items-center" data-id="element-2808">
                            <input type="radio" name="triedIncognito" value="no" checked={formData.triedIncognito === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2809" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2810">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                      <div data-id="element-2811">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2812">
                          Have you cleared your browser cache and cookies?
                        </label>
                        <div className="flex space-x-4" data-id="element-2813">
                          <label className="inline-flex items-center" data-id="element-2814">
                            <input type="radio" name="clearedCache" value="yes" checked={formData.clearedCache === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2815" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2816">
                              Yes
                            </span>
                          </label>
                          <label className="inline-flex items-center" data-id="element-2817">
                            <input type="radio" name="clearedCache" value="no" checked={formData.clearedCache === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2818" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2819">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                      <div data-id="element-2820">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2821">
                          Have you disabled browser extensions/add-ons?
                        </label>
                        <div className="flex space-x-4" data-id="element-2822">
                          <label className="inline-flex items-center" data-id="element-2823">
                            <input type="radio" name="disabledExtensions" value="yes" checked={formData.disabledExtensions === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2824" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2825">
                              Yes
                            </span>
                          </label>
                          <label className="inline-flex items-center" data-id="element-2826">
                            <input type="radio" name="disabledExtensions" value="no" checked={formData.disabledExtensions === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2827" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2828">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between" data-id="element-2829">
                      <Button onClick={() => handleNextStep('account-check')} variant="outline" data-id="element-2830">
                        Back
                      </Button>
                      <Button onClick={() => handleNextStep('network-check')} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2831">
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>}
              {currentStep === 'network-check' && <div data-id="element-2832">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2833">
                    <WifiOffIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2834" />
                    Network & Connection Check
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100" data-id="element-2835">
                    <p className="text-sm text-indigo-700" data-id="element-2836">
                      Sometimes network issues or security settings can prevent
                      successful login.
                    </p>
                  </div>
                  <div className="space-y-6" data-id="element-2837">
                    <div data-id="element-2838">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2839">
                        Are you using a VPN or corporate network?
                      </label>
                      <div className="flex space-x-4" data-id="element-2840">
                        <label className="inline-flex items-center" data-id="element-2841">
                          <input type="radio" name="usingVpn" value="yes" checked={formData.usingVpn === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2842" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2843">
                            Yes
                          </span>
                        </label>
                        <label className="inline-flex items-center" data-id="element-2844">
                          <input type="radio" name="usingVpn" value="no" checked={formData.usingVpn === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2845" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2846">No</span>
                        </label>
                      </div>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg bg-white" data-id="element-2847">
                      <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2848">
                        Network Troubleshooting Tips
                      </h3>
                      <ul className="space-y-2 text-sm text-gray-600" data-id="element-2849">
                        <li className="flex items-start" data-id="element-2850">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2851">
                            •
                          </span>
                          <span data-id="element-2852">
                            Try using a different network (switch from WiFi to
                            mobile data)
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-2853">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2854">
                            •
                          </span>
                          <span data-id="element-2855">
                            Restart your router if you're on a home network
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-2856">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2857">
                            •
                          </span>
                          <span data-id="element-2858">
                            Check if your network blocks certain websites or
                            services
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-2859">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2860">
                            •
                          </span>
                          <span data-id="element-2861">
                            Temporarily disable any firewall or security
                            software
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="flex justify-between" data-id="element-2862">
                      <Button onClick={() => handleNextStep('browser-check')} variant="outline" data-id="element-2863">
                        Back
                      </Button>
                      <Button onClick={analyzeProblem} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2864">
                        Find Solutions
                      </Button>
                    </div>
                  </div>
                </div>}
              {currentStep === 'solution' && <div data-id="element-2865">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2866">
                    <CheckCircleIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2867" />
                    Your Personalized Solutions
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100" data-id="element-2868">
                    <p className="text-sm text-indigo-700" data-id="element-2869">
                      Based on your answers, we've identified potential
                      solutions to help you log in.
                    </p>
                  </div>
                  {diagnosis.length > 0 && <div className="mb-6" data-id="element-2870">
                      <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2871">
                        Potential Issues:
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2872">
                        {diagnosis.map((issue, index) => <div key={index} className={`p-3 flex items-start ${index !== diagnosis.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-2873">
                            <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-2874" />
                            <span className="text-sm text-gray-700" data-id="element-2875">
                              {issue}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                  <div className="mb-8" data-id="element-2876">
                    <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2877">
                      Recommended Solutions:
                    </h3>
                    <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2878">
                      {solutions.map((solution, index) => <div key={index} className={`p-3 flex items-start ${index !== solutions.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-2879">
                          <div className="flex-shrink-0 h-5 w-5 text-green-500 flex items-center justify-center mr-2 mt-0.5" data-id="element-2880">
                            {index + 1}
                          </div>
                          <span className="text-sm text-gray-700" data-id="element-2881">
                            {solution}
                          </span>
                        </div>)}
                    </div>
                  </div>
                  <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6" data-id="element-2882">
                    <div className="flex" data-id="element-2883">
                      <ShieldIcon className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" data-id="element-2884" />
                      <div data-id="element-2885">
                        <h3 className="text-sm font-medium text-purple-900 mb-1" data-id="element-2886">
                          Still having trouble?
                        </h3>
                        <p className="text-sm text-purple-700" data-id="element-2887">
                          Our support team is ready to help. Click the chat icon
                          in the bottom right corner or email us at
                          support@sponsrai.se
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between" data-id="element-2888">
                    <Button onClick={() => handleNextStep('initial')} variant="outline" className="flex items-center" data-id="element-2889">
                      <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-2890" />
                      Start Over
                    </Button>
                    <Link to="/login" data-id="element-2891">
                      <Button variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2892">
                        Return to Login
                      </Button>
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
          {/* Common login issues */}
          <div className="mt-12" data-id="element-2893">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center" data-id="element-2894">
              Common Login Issues
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2895">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 relative overflow-hidden" data-id="element-2896">
                <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-50 rounded-full -mr-8 -mt-8 opacity-30" data-id="element-2897"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center" data-id="element-2898">
                  <CookieIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2899" />
                  Browser Storage Issues
                </h3>
                <p className="text-sm text-gray-600 mb-3" data-id="element-2900">
                  Our platform requires cookies and local storage to function
                  properly. Make sure:
                </p>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-2901">
                  <li className="flex items-start" data-id="element-2902">
                    <span className="text-indigo-500 mr-2" data-id="element-2903">•</span>
                    <span data-id="element-2904">Cookies are enabled in your browser</span>
                  </li>
                  <li className="flex items-start" data-id="element-2905">
                    <span className="text-indigo-500 mr-2" data-id="element-2906">•</span>
                    <span data-id="element-2907">You're not in private/incognito browsing mode</span>
                  </li>
                  <li className="flex items-start" data-id="element-2908">
                    <span className="text-indigo-500 mr-2" data-id="element-2909">•</span>
                    <span data-id="element-2910">No privacy extensions are blocking storage</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 relative overflow-hidden" data-id="element-2911">
                <div className="absolute top-0 right-0 w-16 h-16 bg-purple-50 rounded-full -mr-8 -mt-8 opacity-30" data-id="element-2912"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center" data-id="element-2913">
                  <ServerIcon className="h-5 w-5 text-purple-500 mr-2" data-id="element-2914" />
                  Account Security
                </h3>
                <p className="text-sm text-gray-600 mb-3" data-id="element-2915">
                  We take security seriously. Your account might be temporarily
                  locked if:
                </p>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-2916">
                  <li className="flex items-start" data-id="element-2917">
                    <span className="text-purple-500 mr-2" data-id="element-2918">•</span>
                    <span data-id="element-2919">There were too many failed login attempts</span>
                  </li>
                  <li className="flex items-start" data-id="element-2920">
                    <span className="text-purple-500 mr-2" data-id="element-2921">•</span>
                    <span data-id="element-2922">
                      Suspicious activity was detected from your account
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2923">
                    <span className="text-purple-500 mr-2" data-id="element-2924">•</span>
                    <span data-id="element-2925">You're logging in from an unusual location</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
}